import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import { useResetPassword } from '~/features/auth/queries/useAuth'
import Close from '~/shared/assets/icons/Close'
import Eye from '~/shared/assets/icons/Eye'
import Button from '~/shared/components/buttons/Button'
import PasswordValidationCheck from '~/shared/components/modals/auth/PasswordValidationCheck'
import { isModalOpenState, keyCodeState } from '~/shared/states'
import theme from '~/shared/styles/theme'

function PasswordResetModal() {
  const { enqueueSnackbar } = useSnackbar()
  const [_, setIsModalOpen] = useRecoilState(isModalOpenState)
  const [passwordChangeKeyCode] = useRecoilState(keyCodeState)

  const { mutate: resetPasswordMutate } = useResetPassword()

  const [password, setPassword] = useState<string>('')
  const [passwordCheck, setPasswordCheck] = useState<string>('')

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const [isPasswordValidated, setIsPasswordValidated] = useState<boolean>(false)
  const [isPasswordMatched, setIsPasswordMatched] = useState<boolean>(true)

  const sendPasswordChange = () => {
    if (isPasswordValidated && isPasswordMatched) {
      resetPasswordMutate(
        {
          key: passwordChangeKeyCode.key,
          certCode: passwordChangeKeyCode.code,
          new_password: password,
        },
        {
          onSuccess: (res) => {
            if (res.errorCode < 0) {
              enqueueSnackbar(res.errorMsg, { variant: 'error' })
            } else if (res.errorCode === 0) {
              enqueueSnackbar('비밀번호가 변경되었습니다.', {
                variant: 'success',
              })
              setIsModalOpen({ contents: 'sign-in', isOpen: true })
            }
          },
        },
      )
    }
  }

  useEffect(() => {
    if (password === passwordCheck) {
      setIsPasswordMatched(true)
    } else {
      setIsPasswordMatched(false)
    }
  }, [password, passwordCheck])

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <Title>비밀번호 변경하기</Title>
      <Desc>새로운 비밀번호를 설정하세요.</Desc>

      <InputBox>
        <div>
          <PasswordLabel>
            <InputTitle>새로운 비밀번호 입력</InputTitle>
            <Input
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
            />
            <EyeIcon
              color={
                isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
              }
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            />
          </PasswordLabel>
          <PasswordValidationCheck
            passwordInput={password || ''}
            setIsPasswordValidated={setIsPasswordValidated}
          />
        </div>
        <PasswordLabel>
          <InputTitle>비밀번호 확인</InputTitle>
          <Input
            type={isPasswordVisible ? 'text' : 'password'}
            placeholder="Password"
            className={isPasswordMatched ? '' : 'warning'}
            onChange={(e) => setPasswordCheck(e.target.value)}
          />

          <EyeIcon
            color={
              isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
            }
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
          />
        </PasswordLabel>
        <InputDesc className={`${!isPasswordMatched && 'warning'}`}>
          {!isPasswordMatched && '*패스워드가 일치하지 않습니다.'}
        </InputDesc>
      </InputBox>
      <SendButton
        $width={530}
        $height={48}
        $fontSize={18}
        $variant="black"
        onClick={sendPasswordChange}
      >
        완료
      </SendButton>
    </Wrapper>
  )
}

export default PasswordResetModal

const Wrapper = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 684px;
  height: auto;
  margin: 0 auto;
  padding: 108px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    gap: 24px;
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const CloseIcon = styled(Close)`
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const Desc = styled.div`
  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  line-height: 25px;
  text-align: center;
`

const InputBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const InputTitle = styled.div`
  bottom: 12px;

  margin: 12px 0;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  color: ${theme.colors.grey_4};
`

const PasswordLabel = styled.label`
  position: relative;
`

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Light, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border: none;
  border-radius: 12px;

  &.warning {
    border: 1px solid ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const InputDesc = styled.div`
  display: flex;
  align-items: center;

  margin: 12px 0;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;

  &.warning {
    color: ${theme.colors.warning};
  }

  &.ok {
    color: ${theme.colors.mint_3};
  }
`

const EyeIcon = styled(Eye)`
  position: absolute;
  top: 70%;
  right: 20px;
  transform: translateY(-50%);
`

const SendButton = styled(Button)`
  width: 100%;
  color: ${theme.colors.white};
  background-color: ${theme.colors.grey_5};

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`
